import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class OverlayCloseOnScrollService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly teardownCallbacks: Array<() => void> = [];

  private initialized = false;
  private closeScheduled = false;

  private readonly overlayContentSelector = [
    '.cdk-overlay-pane .ant-select-dropdown:not(.ant-select-dropdown-hidden)',
    '.cdk-overlay-pane .ant-dropdown:not(.ant-dropdown-hidden)',
    '.cdk-overlay-pane .ant-picker-dropdown:not(.ant-picker-dropdown-hidden)',
    '.cdk-overlay-pane .ant-cascader-menus',
    '.cdk-overlay-pane .ant-mention-dropdown',
    '.cdk-overlay-pane .ant-auto-complete'
  ].join(', ');

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.addCaptureListener('scroll', this.handleViewportActivity, true);
    this.addCaptureListener('wheel', this.handleViewportActivity, true);
    this.addCaptureListener('touchmove', this.handleViewportActivity, true);
  }

  ngOnDestroy(): void {
    while (this.teardownCallbacks.length > 0) {
      this.teardownCallbacks.pop()?.();
    }
  }

  private addCaptureListener<K extends keyof DocumentEventMap>(
    type: K,
    listener: (event: DocumentEventMap[K]) => void,
    capture: boolean
  ): void {
    this.document.addEventListener(type, listener as EventListener, capture);
    this.teardownCallbacks.push(() => this.document.removeEventListener(type, listener as EventListener, capture));
  }

  private readonly handleViewportActivity = (event: Event): void => {
    const target = event.target;
    if (target instanceof Element && target.closest('.cdk-overlay-pane')) {
      return;
    }

    if (!this.hasClosableOverlay()) {
      return;
    }

    if (this.closeScheduled) {
      return;
    }

    this.closeScheduled = true;
    requestAnimationFrame(() => {
      this.closeScheduled = false;
      this.closeOverlayByOutsideClick();
    });
  };

  private hasClosableOverlay(): boolean {
    return !!this.document.querySelector(this.overlayContentSelector);
  }

  private closeOverlayByOutsideClick(): void {
    const body = this.document.body;
    if (!body) {
      return;
    }

    body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window }));
    body.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window }));
    body.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  }
}
